import isString from 'lodash/isString';
import isArray from 'lodash/isArray';

const MIN_LENGTH = 8;
const MAX_LENGTH = 200;
const UPPER_CASE_REGEX = /.*[A-Z].*/;
const LOWER_CASE_REGEX = /.*[a-z].*/;
const DIGIT_REGEX = /.*\d+.*/;
const SPECIAL_CHAR_REGEX = /.*[!@#$%^&*_+{}|:"<>?`\-=[\]\\;',./].*/;
const RESTRICTED_WORDS = [
  '12345',
  'abcdef',
  'abstract',
  'acceso',
  'access',
  'accesso',
  'account',
  'acesso',
  'admin',
  'amsterdam',
  'asdfgh',
  'biblio',
  'clave',
  'college',
  'consult',
  'contrasena',
  'elsevier',
  'issue',
  'journal',
  'library',
  'nature',
  'password',
  'professor',
  'qwerty',
  'school',
  'science',
  'scopus',
  'student',
  'universi',
  'zxcvbn',
];

export const PASSWORD_ERRORS = {
  REQUIRED: 'required',
  LOWER_CASE: 'lowerCase',
  UPPER_CASE: 'upperCase',
  DIGIT_OR_SPECIAL: 'digitOrSpecial',
  MIN_LENGTH: 'minLength',
  MAX_LENGTH: 'maxLength',
  RESTRICT: 'restrict',
  SEQUENCE: 'sequence',
  CHECK_ASCII: 'checkASCII',
  COMPARE_TO: 'compareTo',
  CONFIRM_PASSWORD_REQUIRED: 'confirmPasswordRequired',
};

// eslint-disable-next-line consistent-return
const checkASCIIError = (password) => {
  if (!isString(password)) {
    return true;
  }
  const stringLength = password.length;
  let charCode;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < stringLength; i++) {
    charCode = password.charCodeAt(i);
    if (charCode < 33 || charCode > 126) {
      return true;
    }
  }
};

const checkRestrictErrorWithExtraWords = (password, restrictedWords) => {
  let containRestrictedWord = false;

  if (!isString(password)) {
    return !containRestrictedWord;
  }

  const passwordLowerCase = password.toLowerCase();

  restrictedWords.forEach((word) => {
    if (isString(word)) {
      const reversedWord = word.split('').reverse().join('').toLowerCase();

      if (passwordLowerCase.indexOf(reversedWord) !== -1 || passwordLowerCase.indexOf(word.toLowerCase()) !== -1) {
        containRestrictedWord = true;
      }
    }
  });

  return containRestrictedWord;
};

const checkRestrictError = (password) => {
  let containRestrictedWord = false;
  const allRestrictedWords = RESTRICTED_WORDS;

  if (!isString(password)) {
    return !containRestrictedWord;
  }

  const passwordLowerCase = password.toLowerCase();

  allRestrictedWords.forEach((word) => {
    // eslint-disable-next-line no-param-reassign
    word = word.toLowerCase();
    if (passwordLowerCase.indexOf(word) !== -1) {
      containRestrictedWord = true;
    }
  });

  return containRestrictedWord;
};

const increaseSequence = (sequence) => {
  const sequenceLength = sequence.length;
  let result = sequence;
  let i;

  // eslint-disable-next-line
  for (i = 0; i < sequenceLength; i++) {
    result = result.substr(0, i) + String.fromCharCode(result.substr(i, 1).charCodeAt(0) + 1) + result.substr(i + 1);
  }
  return result;
};

const checkSequence = (target, startSequence, breakCode) => {
  let containSequence = false;
  let sequence = startSequence;
  const sequenceLength = startSequence.length;

  // eslint-disable-next-line no-param-reassign
  target = target.toLowerCase();

  while (!containSequence && sequence.charCodeAt(sequenceLength - 1) !== breakCode + 1) {
    if (target.indexOf(sequence) !== -1) {
      containSequence = true;
    }
    sequence = increaseSequence(sequence);
  }

  return containSequence;
};

const checkSequenceError = (password) => {
  if (!password) {
    return false;
  }

  return checkSequence(password, '1234', '9'.charCodeAt(0)) || checkSequence(password, 'abcd', 'z'.charCodeAt(0));
};

export const validateConfirmationPassword = (password, confirmationPassword) => {
  const currentErrors = [];

  if (!confirmationPassword) {
    currentErrors.push(PASSWORD_ERRORS.CONFIRM_PASSWORD_REQUIRED);
  }

  if (confirmationPassword && password !== confirmationPassword) {
    currentErrors.push(PASSWORD_ERRORS.COMPARE_TO);
  }

  return {
    isValid: currentErrors.length === 0,
    errors: currentErrors,
  };
};

export const validatePassword = (password, extraRestrictedWords) => {
  const currentErrors = [];

  if (!password) {
    currentErrors.push(PASSWORD_ERRORS.REQUIRED);
  }

  if (!LOWER_CASE_REGEX.test(password)) {
    currentErrors.push(PASSWORD_ERRORS.LOWER_CASE);
  }

  if (!UPPER_CASE_REGEX.test(password)) {
    currentErrors.push(PASSWORD_ERRORS.UPPER_CASE);
  }

  if (!(DIGIT_REGEX.test(password) || SPECIAL_CHAR_REGEX.test(password))) {
    currentErrors.push(PASSWORD_ERRORS.DIGIT_OR_SPECIAL);
  }

  if (!password || password.length < MIN_LENGTH) {
    currentErrors.push(PASSWORD_ERRORS.MIN_LENGTH);
  }

  if (!password || password.length > MAX_LENGTH) {
    currentErrors.push(PASSWORD_ERRORS.MAX_LENGTH);
  }

  if (checkASCIIError(password)) {
    currentErrors.push(PASSWORD_ERRORS.CHECK_ASCII);
  }

  if (extraRestrictedWords) {
    if (checkRestrictErrorWithExtraWords(password, extraRestrictedWords)) {
      currentErrors.push(PASSWORD_ERRORS.RESTRICT);
    }
  }

  if (!extraRestrictedWords) {
    if (checkRestrictError(password)) {
      currentErrors.push(PASSWORD_ERRORS.RESTRICT);
    }
  }

  if (checkSequenceError(password)) {
    currentErrors.push(PASSWORD_ERRORS.SEQUENCE);
  }

  return {
    isValid: currentErrors.length === 0,
    errors: currentErrors,
  };
};

export const createValidatePasswordWithExtraRestrictedWords = (extraRestrictedWords) => {
  let allRestrictedWords = RESTRICTED_WORDS;

  if (isArray(extraRestrictedWords)) {
    extraRestrictedWords.forEach((word) => {
      // eslint-disable-next-line no-param-reassign
      word = word.replace(' ', '');
      extraRestrictedWords.unshift(word);
    });

    allRestrictedWords = allRestrictedWords.concat(extraRestrictedWords);
  }

  return (password) => validatePassword(password, allRestrictedWords);
};
